// Public-profile routing helpers, shared by every avatar in the app so they all
// agree on where a user "lives". `/profile` is the signed-in user; `/@handle`
// is anyone — the handle is the nickname (stored without the "@"), with the
// hashed user id as a fallback for the few payloads that carry no nickname (the
// backend resolves both). `/@handle` shares the `/:slug` route with communities:
// SlugRoute tells them apart by this prefix.

export const PROFILE_HANDLE_PREFIX = '@';

// The handle inside a `/:slug` segment, or null when the segment is a
// community slug. React Router hands the segment over already decoded.
export function profileHandleFromSlug(slug) {
    if (typeof slug !== 'string' || !slug.startsWith(PROFILE_HANDLE_PREFIX)) return null;
    const handle = slug.slice(PROFILE_HANDLE_PREFIX.length);
    return handle ? handle : null;
}

// Resolves the user id out of the loose user shapes that flow around the app
// (`id` on user/publisher objects, `user_id` on membership/comment/chat rows).
export function resolveUserId(user) {
    if (!user) return null;
    const id = user.id ?? user.user_id ?? null;
    return id === '' ? null : id;
}

// The URL segment for a user: nickname first, hashed id second.
export function resolveHandle(userOrHandle) {
    if (userOrHandle === null || userOrHandle === undefined || userOrHandle === '') return null;
    if (typeof userOrHandle !== 'object') return String(userOrHandle);
    const nickname = typeof userOrHandle.nickname === 'string' ? userOrHandle.nickname.trim() : '';
    if (nickname) return nickname;
    const id = resolveUserId(userOrHandle);
    return id === null || id === undefined ? null : String(id);
}

export function profilePath(userOrHandle) {
    const handle = resolveHandle(userOrHandle);
    return handle ? `/${PROFILE_HANDLE_PREFIX}${encodeURIComponent(handle)}` : '/profile';
}

export function openProfile(userOrHandle) {
    window.navigate?.(profilePath(userOrHandle));
}

// Props that turn any element (a post header, a chat header, an owner line)
// into a keyboard-reachable link to the user's profile. Empty when the user
// can't be resolved, so `<div {...profileLinkProps(user)}>` is always safe.
// The click stops propagating: these blocks usually sit inside rows/cards with
// their own destination, and the profile wins.
export function profileLinkProps(user) {
    if (resolveHandle(user) === null) return {};

    const go = (e) => {
        e.stopPropagation();
        openProfile(user);
    };

    return {
        role: 'link',
        tabIndex: 0,
        title: user?.username || undefined,
        onClick: go,
        onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                go(e);
            }
        },
    };
}

export function isProfileLinkable(user) {
    return resolveHandle(user) !== null;
}
